/**
 * Barra de acciones: deshacer, rehacer, vaciar la rejilla y acusar.
 *
 * «Vaciar» pide confirmación en dos pasos en vez de `window.confirm`: es probable en
 * móvil, se puede probar y no bloquea el hilo.
 */

import { useState } from 'react';
import { Gavel, Redo2, Trash2, Undo2 } from 'lucide-react';

export interface CaseToolbarProps {
  canUndo: boolean;
  canRedo: boolean;
  errors: number;
  /**
   * Nombre de quien el jugador ha puesto en la casilla del crimen, o `null` si sigue
   * vacía. Con estos ejes la condición de «se puede acusar» es exactamente eso: hay
   * alguien en la casilla donde aparece el cuerpo.
   */
  accused: string | null;
  onUndo: () => void;
  onRedo: () => void;
  onClearGrid: () => void;
}

export function CaseToolbar({
  canUndo,
  canRedo,
  errors,
  accused,
  onUndo,
  onRedo,
  onClearGrid,
}: CaseToolbarProps) {
  const [confirming, setConfirming] = useState(false);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <ToolButton label="Deshacer" disabled={!canUndo} onClick={onUndo}>
        <Undo2 size={18} aria-hidden="true" />
      </ToolButton>
      <ToolButton label="Rehacer" disabled={!canRedo} onClick={onRedo}>
        <Redo2 size={18} aria-hidden="true" />
      </ToolButton>

      {confirming ? (
        <span className="border-border flex items-center gap-2 rounded-md border px-2 py-1 text-sm">
          <span>¿Vaciar la rejilla?</span>
          <button
            type="button"
            onClick={() => {
              onClearGrid();
              setConfirming(false);
            }}
            className="focus-ring text-crime min-h-11 px-2 font-medium"
          >
            Sí, vaciar
          </button>
          <button
            type="button"
            onClick={() => setConfirming(false)}
            className="focus-ring text-text-muted min-h-11 px-2"
          >
            Cancelar
          </button>
        </span>
      ) : (
        <ToolButton label="Vaciar rejilla" onClick={() => setConfirming(true)}>
          <Trash2 size={18} aria-hidden="true" />
        </ToolButton>
      )}

      <span className="text-text-muted ml-auto text-sm" aria-live="polite">
        Errores: <strong className="font-mono">{errors}</strong>
      </span>

      {/*
       * TODO(MON-60, etapa 4): al pulsar, confirmación → veredicto → tarjeta
       * compartible. La condición de habilitado ya es la definitiva —hay alguien en la
       * casilla del crimen— pero el botón sigue deshabilitado a propósito hasta MON-60.
       */}
      <button
        type="button"
        disabled
        title={
          accused === null
            ? 'Pon a alguien en la casilla del cuerpo para poder acusar'
            : `Acusar a ${accused} — disponible en la siguiente entrega (MON-60)`
        }
        className="border-border text-text-muted flex min-h-11 items-center gap-2 rounded-md border px-3 text-sm opacity-50"
      >
        <Gavel size={18} aria-hidden="true" />
        {accused === null ? 'Acusar' : `Acusar a ${accused.split(' ')[0] ?? ''}`}
      </button>
    </div>
  );
}

function ToolButton({
  label,
  disabled = false,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="border-border text-text hover:bg-surface-raised focus-ring grid h-11 w-11 place-items-center rounded-md border disabled:opacity-40"
    >
      {children}
    </button>
  );
}
